import { Link, useLocation } from 'react-router-dom';
import { Music, Radio, Calendar, Users, Disc, Home, ShoppingBag, BookOpen, Mail, Menu, X, User, LogOut, Crown, Settings, Ticket, Mic2, MapPin, ChevronDown, Headphones, Newspaper, Star, Megaphone, Send, Zap, Globe, TrendingUp, Trophy, ListMusic, Film, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useGeoCountry } from '../hooks/useGeoCountry';
import NotificationBell from './NotificationBell';
import ContinueListeningMenu from './ContinueListeningMenu';
import { canAccessFeature, canEnterAdminWorkspace } from '../lib/adminAccessRuntime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// Dropdown Component
const NavDropdown = ({ label, icon: Icon, items, isActive }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const menuId = `nav-${label.toLowerCase().replace(/\s+/g, '-')}-menu`;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="menu"
        className={`flex items-center gap-1 px-3 py-2 rounded-sm font-medium text-sm transition-colors duration-300 ${
          isActive
            ? 'bg-primary/20 text-primary'
            : 'text-foreground/80 hover:text-primary hover:bg-white/5'
        }`}
      >
        {Icon && <Icon className="w-4 h-4" />}
        {label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            id={menuId}
            role="menu"
            className="absolute top-full left-0 mt-1 max-h-[calc(100dvh-5rem)] w-56 overflow-y-auto overscroll-contain py-2 bg-card border border-white/10 rounded-xl shadow-2xl z-50"
          >
            {items.map((item, idx) => {
              if (item.divider) {
                return <div key={`div-${idx}`} className="my-1 border-t border-white/10" />;
              }
              const ItemIcon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setOpen(false)}
                  role="menuitem"
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                  className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${location.pathname === item.path ? 'bg-primary/15 text-primary' : 'hover:bg-white/5'}`}
                >
                  {ItemIcon && <ItemIcon className="w-4 h-4 text-muted-foreground" />}
                  <span className="flex-1">{item.label}</span>
                  {item.badge && (
                    <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-rastyle-orange bg-rastyle-orange/10 border border-rastyle-orange/40 rounded-full px-1.5 py-0.5">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Navigation = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout, loading: authLoading } = useAuth();
  const geo = useGeoCountry();
  const showAfrikaShop = geo.is_kenya || geo.is_afrika_shop_region;
  const adminAccess = user?.admin_access;
  const hasStaffWorkspace = canEnterAdminWorkspace(user);
  const canUseWhatsAppInbox = hasStaffWorkspace
    && canAccessFeature(adminAccess, 'communications.whatsapp.inbox');
  const [logo, setLogo] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/settings/logo`);
        if (response.data?.value) {
          setLogo(response.data.value);
        }
      } catch (error) {
        // Use default logo if fetch fails
        setLogoError(true);
      }
    };
    fetchLogo();
    // Listen for logo updates from admin panel
    const onLogoUpdate = (e) => { if (e.detail) setLogo(e.detail); };
    window.addEventListener('rastyle-logo-updated', onLogoUpdate);
    return () => window.removeEventListener('rastyle-logo-updated', onLogoUpdate);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        setUserMenuOpen(false);
      }
    };
    const onPointerDown = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [mobileMenuOpen]);

  // Grouped navigation structure
  const musicItems = [
    { path: '/mixtapes', label: 'Mixtapes', icon: Music },
    { path: '/video-mixes', label: 'Video Mixes', icon: Film },
    { path: '/playlists', label: 'Playlists', icon: ListMusic },
    { divider: true },
    { path: '/records', label: 'Rastyle Records', icon: Disc },
    { divider: true },
    { path: '/radio', label: 'Live Radio', icon: Radio },
  ];

  const discoverItems = [
    { path: '/aktivate', label: 'Aktivate Network', icon: Zap },
    { path: '/atlas', label: 'Reggae Atlas', icon: Globe },
    { path: '/radar', label: 'Reggae Radar', icon: TrendingUp },
    { path: '/creators', label: 'Creators', icon: Mic2 },
    { path: '/community', label: 'Community', icon: Users },
    // Iter 217.2 — split directory entries for tags-aware browsing
    { path: '/artists', label: 'Artists', icon: Mic2 },
    { path: '/deejays', label: 'Deejays', icon: Disc },
    ...(showAfrikaShop
      ? [{ path: '/afrika-shop', label: 'Afrika Shop (KES)', icon: ShoppingBag, badge: 'KE' }]
      : []),
    { path: '/shop', label: showAfrikaShop ? 'Global Merch Shop' : 'Merch Shop', icon: ShoppingBag },
    { path: '/odds', label: 'Odds Room', icon: Trophy },
    { path: '/creator-leaderboard', label: 'Creator Leaderboard', icon: Trophy },
    { path: '/trivia', label: 'Trivia Games', icon: Calendar },
    { path: '/leaderboard', label: 'Leaderboard', icon: Crown },
    { path: '/rewards', label: 'Rewards', icon: Star },
    // Iter 187: low-UAU utilities remain reachable directly:
    //   /heatmap, /gaming, /missions
  ];

  const moreItems = [
    { path: '/ras-fest', label: 'Ras-Fest 2026', icon: Calendar },
    { path: '/about', label: 'About Us', icon: BookOpen },
    { path: '/contact', label: 'Contact', icon: Mail },
  ];

  // Check if any dropdown item is active
  const isMusicActive = musicItems.some(item => item.path && location.pathname === item.path);
  const isDiscoverActive = discoverItems.some(item => location.pathname === item.path);
  const isMoreActive = moreItems.some(item => location.pathname === item.path);

  // Five destinations fit safely once the universal search launcher is
  // portalled into this bar: four routes + Search.
  const mobileNavItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/explore', label: 'Explore', icon: Globe },
    { path: '/events', label: 'Events', icon: MapPin },
    { path: showAfrikaShop ? '/afrika-shop' : '/shop', label: 'Shop', icon: ShoppingBag },
  ];

  const mobileSections = [
    {
      label: 'Featured',
      items: [
        { path: '/events', label: 'Reggae In Town', icon: MapPin, featured: true },
        { path: '/ras-fest', label: 'Ras-Fest 2026', icon: Calendar, featured: true },
        { path: '/aktivate', label: 'Aktivate Network', icon: Zap },
      ],
    },
    {
      label: 'Listen & watch',
      items: [
        { path: '/radio', label: 'Live Radio', icon: Radio },
        { path: '/mixtapes', label: 'Mixtapes', icon: Music },
        { path: '/video-mixes', label: 'Video Mixes', icon: Film },
        { path: '/playlists', label: 'Playlists', icon: ListMusic },
        { path: '/records', label: 'New Releases', icon: Disc },
      ],
    },
    {
      label: 'Discover culture',
      items: [
        { path: '/creators', label: 'Creators', icon: Mic2 },
        { path: '/community', label: 'Community', icon: Users },
        { path: '/atlas', label: 'Reggae Atlas', icon: Globe },
        { path: '/radar', label: 'Reggae Radar', icon: TrendingUp },
        { path: '/trivia', label: 'Trivia Games', icon: Trophy },
        { path: '/rewards', label: 'Rewards', icon: Star },
      ],
    },
    {
      label: 'Shop & information',
      items: [
        ...(showAfrikaShop ? [{ path: '/afrika-shop', label: 'Afrika Shop (KES)', icon: ShoppingBag, featured: true }] : []),
        { path: '/shop', label: showAfrikaShop ? 'Global Merch Shop (USD)' : 'Merch Shop (USD)', icon: ShoppingBag },
        { path: '/about', label: 'About Us', icon: BookOpen },
        { path: '/contact', label: 'Contact', icon: Mail },
      ],
    },
  ];

  // Signed-in creator and staff tools.
  const accountMobileItems = isAuthenticated ? [
    ...(hasStaffWorkspace
      ? [{ path: '/admin', label: 'Open Staff Workspace', icon: Crown }]
      : []),
    ...(user?.creator_status
      ? [
        { path: '/creator/dashboard', label: 'Creator Dashboard', icon: Mic2 },
        ...(user.creator_status === 'approved' ? [
          { path: '/social-studio', label: 'Social Studio', icon: Send },
          { path: '/promote', label: 'Promote & Grow', icon: Megaphone },
        ] : []),
      ]
      : [{ path: '/become-creator', label: 'Become a Creator', icon: Mic2 }]),
    ...(canUseWhatsAppInbox
      ? [{ path: '/staff/whatsapp-inbox', label: 'WhatsApp Inbox', icon: MessageCircle }]
      : []),
  ] : [];

  return (
    <>
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      data-testid="main-site-nav"
      className="fixed top-0 left-0 right-0 z-50 glass-nav"
      aria-label="Primary navigation"
    >
      <div className="max-w-[90rem] mx-auto px-3 sm:px-5 xl:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3" data-testid="logo-link" aria-label="Rastyle Republik home">
            {logo && !logoError ? (
              <img
                src={logo}
                alt="Rastyle Republik Kenya"
                className="h-9 w-auto max-w-[8rem] object-contain sm:h-10 sm:max-w-[11rem]"
                onError={() => setLogoError(true)}
                data-testid="logo-img"
              />
            ) : (
              <span
                className="truncate font-heading text-base font-extrabold uppercase tracking-tight text-rastyle-orange sm:text-xl"
                data-testid="logo-text-fallback"
              >
                Rastyle Republik
              </span>
            )}
            {/* Wordmark only shows alongside the IMAGE logo; for the text fallback it's already the brand */}
            {logo && !logoError && (
              <span className="hidden font-heading text-lg font-bold tracking-tight 2xl:block">RASTYLE REPUBLIK</span>
            )}
            <span
              className="hidden items-center gap-1 rounded border border-mpesa-green/40 bg-mpesa-green/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-mpesa-green sm:inline-flex"
              data-testid="kenya-pill"
            >
              {geo.country_code || 'KE'} · {showAfrikaShop ? 'KES' : 'USD'}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center space-x-1">
            {/* Home */}
            <Link
              to="/"
              data-testid="nav-home"
              className={`px-3 py-2 rounded-sm font-medium text-sm transition-colors duration-300 ${
                location.pathname === '/'
                  ? 'bg-primary text-black'
                  : 'text-foreground/80 hover:text-primary hover:bg-white/5'
              }`}
            >
              Home
            </Link>

            {/* Reggae In Town - Featured */}
            <Link
              to="/events"
              data-testid="nav-reggae-in-town"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-sm font-medium text-sm transition-all duration-300 ${
                location.pathname === '/events'
                  ? 'bg-gradient-to-r from-red-600 via-yellow-500 to-green-600 text-black'
                  : 'text-primary hover:bg-primary/10 border border-primary/30'
              }`}
            >
              <MapPin className="w-4 h-4" />
              Reggae In Town
            </Link>

            {/* Music Dropdown */}
            <NavDropdown 
              label="Music" 
              icon={Headphones}
              items={musicItems} 
              isActive={isMusicActive}
            />

            {/* Discover Dropdown */}
            <NavDropdown 
              label="Discover" 
              icon={Users}
              items={discoverItems} 
              isActive={isDiscoverActive}
            />

            {/* More Dropdown */}
            <NavDropdown 
              label="More" 
              items={moreItems} 
              isActive={isMoreActive}
            />
          </div>

          {/* Right Side - Auth */}
          <div className="flex shrink-0 items-center gap-1 sm:gap-2" data-testid="desktop-nav-actions">
            <div className="hidden sm:block"><ContinueListeningMenu /></div>
            {!authLoading && (
              isAuthenticated && user ? (
                <>
                  <NotificationBell />
                  <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 rounded-lg bg-white/5 px-2 py-2 transition-colors hover:bg-white/10 sm:px-3"
                    data-testid="user-menu-btn"
                    aria-expanded={userMenuOpen}
                    aria-controls="site-user-menu"
                    aria-haspopup="menu"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span className="hidden md:block text-sm font-medium">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-3 h-3 hidden md:block" />
                  </button>
                  
                  {/* User Dropdown */}
                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        id="site-user-menu"
                        role="menu"
                        className="absolute right-0 mt-2 max-h-[calc(100dvh-5rem)] w-64 overflow-y-auto overscroll-contain rounded-xl border border-white/10 bg-card py-2 shadow-2xl"
                        data-testid="user-dropdown"
                      >
                        <div className="px-4 py-3 border-b border-white/10">
                          <p className="text-sm font-semibold truncate">{user.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          {hasStaffWorkspace && (
                            <span className="mt-2 inline-flex rounded-full border border-emerald-400/25 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300">
                              Staff access active
                            </span>
                          )}
                          <Link
                            to="/membership"
                            onClick={() => setUserMenuOpen(false)}
                            className={`text-xs mt-1 capitalize inline-flex items-center gap-1 hover:underline ${
                              user.membership_tier === 'gold' ? 'text-yellow-400' :
                              user.membership_tier === 'silver' ? 'text-gray-300' :
                              'text-amber-600'
                            }`}
                          >
                            <Crown className="w-3 h-3" />
                            {user.membership_tier || 'Bronze'} Member
                          </Link>
                        </div>
                        
                        <div className="py-1">
                          <Link
                            to="/rewards"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                            data-testid="nav-rewards"
                          >
                            <Star className="w-4 h-4 text-yellow-400" />
                            Rewards
                          </Link>
                          {user.creator_status === 'approved' && (
                            <>
                              <Link
                                to="/social-studio"
                                onClick={() => setUserMenuOpen(false)}
                                className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                                data-testid="nav-social-studio"
                              >
                                <Send className="w-4 h-4 text-sky-400" />
                                Social Studio
                              </Link>
                              <Link
                                to="/promote"
                                onClick={() => setUserMenuOpen(false)}
                                className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                                data-testid="nav-promote"
                              >
                                <Megaphone className="w-4 h-4 text-orange-400" />
                                Promote & Grow
                              </Link>
                            </>
                          )}
                          <Link
                            to="/membership"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                          >
                            <Crown className="w-4 h-4 text-primary" />
                            Upgrade Membership
                          </Link>
                          <Link
                            to="/profile"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                            data-testid="profile-link"
                          >
                            <Settings className="w-4 h-4" />
                            Profile Settings
                          </Link>
                          <Link
                            to="/my-tickets"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                            data-testid="my-tickets-link"
                          >
                            <Ticket className="w-4 h-4" />
                            My Tickets
                          </Link>
                        </div>
                        
                        <div className="border-t border-white/10 pt-1">
                          {hasStaffWorkspace && (
                            <Link
                              to="/admin"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                              data-testid="switch-admin-dashboard"
                            >
                              <Crown className="w-4 h-4 text-emerald-400" />
                              Open Staff Workspace
                            </Link>
                          )}
                          {canUseWhatsAppInbox && (
                            <Link
                              to="/staff/whatsapp-inbox"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                              data-testid="whatsapp-inbox-link"
                            >
                              <MessageCircle className="w-4 h-4 text-emerald-400" />
                              WhatsApp Inbox
                            </Link>
                          )}
                          {user.creator_status && (
                            <Link
                              to="/creator/dashboard"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                              data-testid="switch-creator-dashboard"
                            >
                              <Mic2 className="w-4 h-4 text-primary" />
                              {user.creator_status === 'approved' ? 'Creator Dashboard' : 'Creator Application'}
                            </Link>
                          )}
                          {!user.creator_status && (
                            <Link
                              to="/become-creator"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                            >
                              <Mic2 className="w-4 h-4 text-primary" />
                              Become a Creator
                            </Link>
                          )}
                          <button
                            onClick={() => { logout(); setUserMenuOpen(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-white/5 transition-colors"
                            data-testid="logout-btn"
                          >
                            <LogOut className="w-4 h-4" />
                            Sign Out
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="mobile-login-cta"
                    className="rounded-full bg-primary px-3 py-2 text-[11px] font-black uppercase tracking-wide text-black sm:px-4 md:hidden"
                  >
                    Join / Login
                  </Link>
                  <Link
                    to="/login"
                    data-testid="login-cta"
                    className="hidden md:block px-5 py-2 bg-primary text-black font-bold text-sm rounded-sm hover:bg-primary/90 neon-gold-shadow transition-all duration-300"
                  >
                    Sign In
                  </Link>
                </>
              )
            )}
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="rounded-lg p-2 transition-colors hover:bg-white/10 xl:hidden"
              data-testid="mobile-menu-toggle"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation-drawer"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

    </motion.nav>

    <AnimatePresence>
      {mobileMenuOpen && (
        <>
          <motion.button
            type="button"
            aria-label="Close navigation menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
            className="fixed inset-0 top-16 z-[80] bg-black/80 xl:hidden"
          />
          <motion.aside
            id="mobile-navigation-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
            className="fixed bottom-0 right-0 top-16 z-[90] w-full overflow-y-auto overscroll-contain border-l border-white/10 bg-black pb-[calc(5rem+env(safe-area-inset-bottom))] shadow-2xl sm:max-w-md xl:hidden"
            data-testid="mobile-navigation-drawer"
          >
            <div className="border-b border-white/10 px-5 py-4">
              <p className="font-heading text-lg font-extrabold uppercase tracking-tight">Explore Rastyle</p>
              <p className="mt-1 text-xs text-zinc-500">Music, events, creators and culture in one place.</p>
            </div>
            <div className="space-y-6 px-4 py-5">
              {mobileSections.map((section) => (
                <section key={section.label} aria-labelledby={`mobile-${section.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  <h2 id={`mobile-${section.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`} className="mb-2 px-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600">{section.label}</h2>
                  <div className="grid grid-cols-2 gap-2">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          aria-current={isActive ? 'page' : undefined}
                          className={`flex min-h-14 items-center gap-3 rounded-xl border px-3 py-3 text-sm transition-colors ${
                            isActive
                              ? 'border-primary bg-primary text-black'
                              : item.featured
                                ? 'border-primary/30 bg-primary/10 text-primary'
                                : 'border-white/5 bg-white/[0.03] text-zinc-300 hover:border-white/15 hover:bg-white/[0.06]'
                          }`}
                        >
                          <Icon className="h-5 w-5 shrink-0" />
                          <span className="font-medium leading-tight">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </section>
              ))}

              {accountMobileItems.length > 0 && (
                <section aria-labelledby="mobile-account-tools">
                  <h2 id="mobile-account-tools" className="mb-2 px-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600">Account tools</h2>
                  <div className="space-y-2">
                    {accountMobileItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm ${isActive ? 'bg-primary text-black' : 'bg-white/[0.03] text-zinc-300'}`}><Icon className="h-5 w-5" /><span className="font-medium">{item.label}</span></Link>;
                    })}
                  </div>
                </section>
              )}

              {!isAuthenticated && <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex h-12 items-center justify-center rounded-full bg-primary font-bold text-black">Sign In / Join Rastyle</Link>}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>

    {/* Mobile Bottom Action Bar — fixed at viewport bottom, OUTSIDE motion.nav so its
        transform doesn't create a new containing block. Stacks under audio player on mobile. */}
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-black xl:hidden"
      data-testid="mobile-bottom-nav"
      aria-label="Quick navigation"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex h-16 items-center justify-around" data-testid="mobile-nav-actions">
        {mobileNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-mobile-${item.label.toLowerCase()}`}
              aria-current={isActive ? 'page' : undefined}
              className={`flex flex-col items-center justify-center gap-1 h-full flex-1 transition-colors duration-200 ${
                isActive ? 'text-rastyle-orange' : 'text-zinc-500 hover:text-white'
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 2} />
              <span className={`text-[10px] uppercase tracking-wider font-mono ${isActive ? 'font-bold' : 'font-medium'}`}>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
    </>
  );
};

export default Navigation;
